/**
 * Cleo ERP SaaS — Dashboard client (cadrage §3.4)
 *
 * Affiche le statut de l'instance, la barre de progression de l'essai,
 * le lien d'accès et les messages selon le statut.
 */
import React, { useEffect, useState } from 'react';
import { Typography, Card, Tag, Button, Progress, Spin, Alert, Space } from 'antd';
import {
  CloudOutlined, GlobalOutlined, CalendarOutlined,
  ClockCircleOutlined, MailOutlined, LoadingOutlined,
} from '@ant-design/icons';
import api from '../../api/axios';

const { Title, Text, Paragraph } = Typography;

// Couleurs des badges de statut (cadrage §3.6)
const STATUS_CONFIG = {
  EN_ATTENTE: { color: '#F59E0B', label: 'En attente', message: 'Votre instance est en cours de préparation...' },
  ACTIF: { color: '#10B981', label: 'Actif', message: 'Votre instance est opérationnelle' },
  'EXPIRÉ': { color: '#EF4444', label: 'Expiré', message: 'Votre période d\'essai est terminée' },
  SUSPENDU: { color: '#64748B', label: 'Suspendu', message: 'Votre compte est suspendu' },
  A_SUPPRIMER: { color: '#991B1B', label: 'À supprimer', message: 'Votre compte sera bientôt supprimé' },
};

function DashboardPage() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/api/tenants/me/')
      .then(({ data }) => setTenant(data))
      .catch((err) => {
        setError(err.response?.data?.detail || 'Impossible de charger les informations.');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 36, color: '#10B981' }} />} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Erreur"
        description={error}
        type="error"
        showIcon
        style={{ marginTop: 24 }}
      />
    );
  }

  if (!tenant) return null;

  const statusConf = STATUS_CONFIG[tenant.status] || STATUS_CONFIG.EN_ATTENTE;

  // Calcul progression essai
  let progressPercent = 0;
  let trialDaysTotal = 0;
  if (tenant.trial_start_date && tenant.trial_end_date) {
    const start = new Date(tenant.trial_start_date);
    const end = new Date(tenant.trial_end_date);
    trialDaysTotal = Math.round((end - start) / (1000 * 60 * 60 * 24));
    const elapsed = trialDaysTotal - (tenant.days_remaining || 0);
    progressPercent = trialDaysTotal > 0 ? Math.round((elapsed / trialDaysTotal) * 100) : 0;
  }

  return (
    <div style={{ paddingTop: 16 }}>
      {/* Card principale */}
      <Card style={{
        borderRadius: 12,
        border: '1px solid #E5E7EB',
        boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
      }}>
        {/* En-tête : nom + statut */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}>
          <Title level={3} style={{ margin: 0, color: '#0F172A' }}>
            {tenant.company_name}
          </Title>
          <Tag
            color={statusConf.color}
            style={{ fontSize: 13, padding: '2px 12px', borderRadius: 6 }}
          >
            {statusConf.label}
          </Tag>
        </div>

        {/* Message statut */}
        <Paragraph style={{ color: '#64748B', marginBottom: 24 }}>
          {statusConf.message}
        </Paragraph>

        {/* Lien instance (si actif) */}
        {tenant.status === 'ACTIF' && (
          <div style={{
            background: '#ECFDF5',
            borderRadius: 8,
            padding: '16px 20px',
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}>
            <div>
              <Text style={{ color: '#064E3B', fontSize: 13 }}>
                Votre instance Cleo ERP
              </Text>
              <br />
              <Text strong style={{ color: '#10B981', fontSize: 16 }}>
                <CloudOutlined style={{ marginRight: 8 }} />
                {tenant.instance_url}
              </Text>
            </div>
            <Button
              type="primary"
              size="large"
              href={tenant.instance_url}
              target="_blank"
              style={{ fontWeight: 600 }}
            >
              Accéder
            </Button>
          </div>
        )}

        {/* Infos essai */}
        {tenant.trial_start_date && (
          <div style={{ marginBottom: 24 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 16,
              marginBottom: 16,
            }}>
              <div>
                <Text style={{ color: '#94A3B8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  Période d'essai
                </Text>
                <br />
                <Text strong style={{ color: '#0F172A', fontSize: 15 }}>
                  <CalendarOutlined style={{ marginRight: 6, color: '#64748B' }} />
                  {trialDaysTotal} jours
                </Text>
              </div>
              <div>
                <Text style={{ color: '#94A3B8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  Expire le
                </Text>
                <br />
                <Text strong style={{ color: '#0F172A', fontSize: 15 }}>
                  <ClockCircleOutlined style={{ marginRight: 6, color: '#64748B' }} />
                  {new Date(tenant.trial_end_date).toLocaleDateString('fr-FR')}
                </Text>
              </div>
              <div>
                <Text style={{ color: '#94A3B8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  Pays
                </Text>
                <br />
                <Text strong style={{ color: '#0F172A', fontSize: 15 }}>
                  <GlobalOutlined style={{ marginRight: 6, color: '#64748B' }} />
                  {tenant.country_code}
                </Text>
              </div>
            </div>

            {/* Barre de progression */}
            {tenant.status === 'ACTIF' && (
              <div>
                <Progress
                  percent={progressPercent}
                  strokeColor={tenant.days_remaining <= 7 ? '#F59E0B' : '#10B981'}
                  trailColor="#E5E7EB"
                  format={() => (
                    <Text style={{
                      color: tenant.days_remaining <= 7 ? '#F59E0B' : '#10B981',
                      fontWeight: 600,
                      fontSize: 13,
                    }}>
                      {tenant.days_remaining} jour{tenant.days_remaining > 1 ? 's' : ''} restant{tenant.days_remaining > 1 ? 's' : ''}
                    </Text>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {/* CTA contact (si non actif) */}
        {tenant.status !== 'ACTIF' && tenant.status !== 'EN_ATTENTE' && (
          <div style={{
            background: '#F8FAFC',
            borderRadius: 8,
            padding: '16px 20px',
            border: '1px solid #E5E7EB',
          }}>
            <Paragraph style={{ color: '#64748B', margin: 0 }}>
              Vous souhaitez continuer à utiliser Cleo ERP ?
            </Paragraph>
            <Button
              type="primary"
              icon={<MailOutlined />}
              href="mailto:infos@ecintelligence.ma"
              style={{ marginTop: 12, fontWeight: 600 }}
            >
              Nous contacter
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

export default DashboardPage;
